import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { VideoHlsService } from './video-hls.service';

interface TileInfo {
  duration: number;
  resolution: {
    width: number;
    height: number;
  };
  numberOfThumbs: number;
  layout: {
    x: number;
    y: number;
  };
  timePerThumb: number;
  startTime: number;
  url: string;
}

export interface ThumbnailInfo {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
  xTiles: number;
}

@Injectable({
  providedIn: 'root',
})
export class VideoThumbnailService {
  http = inject(HttpClient);
  config = inject(AppConfigService);
  hlsService = inject(VideoHlsService);
  loader = inject(DataLoaderService);

  async getThumbnail(
    uuid: string,
    origin: string,
    width: number,
    height: number,
    currentTime: number,
    duration: number
  ) {
    const url = this.hlsService.getHlsUrl(uuid, origin, width, height, duration);
    const manifest = await this.hlsService.loadHls(url, () => false);
    if (!manifest) return;

    const thumbnailManifestUrl = this.getThumbnailManifestUrl(uuid, manifest);
    if (!thumbnailManifestUrl) return;
    const thumbnailManifest = await this.getThumbnailManifest(thumbnailManifestUrl);
    if (!thumbnailManifest) return;

    const tileInfo = this.extractTileInfo(thumbnailManifest);
    return this.calcThumbnailImage(uuid, tileInfo, currentTime);
  }

  private async getThumbnailManifest(url: string) {
    return this.loader.get(url, async () => {
      return lastValueFrom(this.http.get(url, { responseType: 'text' }));
    });
  }

  getThumbnailManifestUrl(uuid: string, manifest: string) {
    const rows = manifest.split('\n');
    const thumbnails = rows.find(row => row.includes('#EXT-X-IMAGE-STREAM-INF'));
    if (!thumbnails) return;
    const urlPart = thumbnails.match(/URI="(.*)"/)[1];
    if (!urlPart) return;
    return `${this.config.assetsEndpoint}/videos/${uuid}/${urlPart}`;
  }

  extractTileInfo(thumbnailManifest: string): TileInfo[] {
    const targetDuration = parseFloat(thumbnailManifest.match(/#EXT-X-TARGETDURATION:(\d+)/)[1]);

    const matches = thumbnailManifest
      .split('\n')
      .map(row => row.trim())
      .join('\n')
      .matchAll(/#EXTINF:(\d+),\n.*RESOLUTION=(\d+)x(\d+),LAYOUT=(\d+)x(\d+),DURATION=(\d+).*\n([^#].*)/g);

    const results: TileInfo[] = [];

    let time = 0;

    for (const match of matches) {
      const [, _duration, _width, _height, _x, _y, _maxTimePerThumb, url] = match;

      const duration = parseFloat(_duration);
      const width = parseInt(_width);
      const height = parseInt(_height);
      const x = parseInt(_x);
      const y = parseInt(_y);
      const maxTimePerThumb = parseInt(_maxTimePerThumb);

      const numberOfThumbs = Math.min(Math.floor(targetDuration / maxTimePerThumb), x * y);

      results.push({
        duration,
        numberOfThumbs,
        resolution: {
          width,
          height,
        },
        layout: {
          x,
          y,
        },
        timePerThumb: targetDuration / numberOfThumbs,
        url,
        startTime: time,
      });
      time += duration;
    }
    return results;
  }

  calcThumbnailImage(uuid: string, tileInfo: TileInfo[], currentTime: number): ThumbnailInfo {
    const tile = tileInfo.find(info => currentTime < info.startTime + info.duration) || tileInfo[tileInfo.length - 1];
    const subTime = currentTime - tile.startTime;
    const thumbIndex = Math.floor(subTime / tile.timePerThumb);

    const thumbX = thumbIndex % tile.layout.x;
    const thumbY = Math.floor(thumbIndex / tile.layout.x);

    return {
      url: `${this.config.assetsEndpoint}/videos/${uuid}/${tile.url}`,
      x: thumbX,
      y: thumbY,
      width: tile.resolution.width,
      height: tile.resolution.height,
      xTiles: tile.layout.x,
    };
  }
}
